<template>
  <div>
    <toggle-panel
      v-model="isQiwiMethodActive"
      title="Qiwi"
      :disabled="isDisabledPaymentOption"
      @click="handleClickOnPanel"
    >
      <qiwi-icon slot="icon" />
    </toggle-panel>

    <cs-dialog v-model="isModalActive" @close="cancelModal">
      <dialog-default-layout>
        <div slot="title" class="modal-title">
          <h2>{{ $t('payment.method.settings') }}</h2>
          <a class="modal-help-icon" href="https://help.capusta.space/payment_page/qiwi" target="_blank"></a>
        </div>

        <template slot="content">
          <div class="input-container">
            <capusta-input
              v-model="merchantPhoneNumber"
              v-mask="'+###############'"
              :validation="validateMerchantPhoneNumber"
              :label="$t('qiwi.wallet.number')"
              :placeholder="$t('enter.number')"
              data-private
            />
          </div>

          <div class="input-container">
            <capusta-input
              v-model="merchantQiwiToken"
              :label="$t('qiwi.token')"
              :placeholder="$t('insert.token')"
              data-private
            >
              <c-link
                v-if="!merchantQiwiToken"
                slot="link"
                href="https://qiwi.com/api"
                target="_blank"
              >
                {{ $t('common.receive') }}
              </c-link>
            </capusta-input>
          </div>
        </template>

        <div slot="footer" class="buttons-container">
          <cs-button :width="buttonWidth" :disabled="!isValidForm" @click="qiwiMethod">
            {{ $t('common.apply') }}
          </cs-button>
          <cs-button :width="buttonWidth" type="transparent" @click="cancelModal">
            {{ $t('common.cancel') }}
          </cs-button>
        </div>
      </dialog-default-layout>
    </cs-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mask } from 'vue-the-mask';

import HTTPService from '@/services/Http';

import CLink from 'atoms/common/Link';
import CapustaInput from 'molecules/capusta-input/CapustaInput';

import QiwiIcon from 'atoms/icons/finance/Qiwi';
import TogglePanel from 'molecules/panels/TogglePanel';

import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';

export default {
  name: 'SettingReceivingPaymentsQiwi',
  directives: {
    mask,
  },
  components: {
    CLink,
    QiwiIcon,
    TogglePanel,
    CapustaInput,
    DialogDefaultLayout,
  },
  props: {
    option: Object,
  },
  created() {
    this.checkActivePaymentOption('qiwi');
  },
  data() {
    return {
      isModalActive: false,
      isQiwiMethodActive: false,
      initialResponseData: null,
      merchantPhoneNumber: '',
      merchantQiwiToken: '',
    };
  },
  computed: {
    buttonWidth() {
      return this.isDesktop ? 'auto' : '100%';
    },
    isValidForm() {
      if (this.$validate.isEmptyString(this.merchantQiwiToken)) {
        return false;
      }

      return this.validateMerchantPhoneNumber();
    },
    isDisabledPaymentOption() {
      return this.option.blocked;
    },
    isUnavailablePaymentOption() {
      return this.option.unavailable;
    },
    ...mapGetters(['isDesktop']),
  },
  methods: {
    validateMerchantPhoneNumber() {
      if (this.merchantPhoneNumber.length < 12) {
        return false;
      }

      if (this.merchantPhoneNumber.length > 16) {
        return false;
      }

      return true;
    },
    toggleMethod() {
      if (this.isQiwiMethodActive) {
        this.disablePaymentOption();
        this.initialResponseData = null;
      } else {
        this.openSettings();
      }
    },
    restoreData() {
      if (!this.initialResponseData) {
        return;
      }
      const { token, phone } = this.initialResponseData;

      this.merchantQiwiToken = token;
      this.merchantPhoneNumber = phone;
    },
    cancelModal() {
      this.isModalActive = false;

      this.restoreData();
    },
    openSettings() {
      this.isModalActive = !this.isModalActive;
    },
    handleClickOnPanel(clickingOnSwitcher) {
      if (this.isDisabledPaymentOption) {
        this.$emit('blocked');
        return;
      }

      if (this.isUnavailablePaymentOption) {
        this.$emit('unavailable');
        return;
      }

      if (clickingOnSwitcher) {
        this.toggleMethod();
        return;
      }

      this.openSettings();
    },
    async qiwiMethod() {
      const payload = {
        phone: this.merchantPhoneNumber,
        token: this.merchantQiwiToken,
      };

      try {
        await HTTPService.activatePaymentOption(payload, 'qiwi');
        this.initialResponseData = payload;

        this.isQiwiMethodActive = true;
        this.isModalActive = false;

        this.$alert({
          type: 'success',
          text: this.$t('payment.method.added'),
        });
      } catch (e) {
        this.$alert({
          type: 'warning',
          text: this.$t('token.or.phone.incorrect'),
        });
      }
    },
    async checkActivePaymentOption(option) {
      if (this.option.unavailable) {
        this.isQiwiMethodActive = false;
        return;
      }

      try {
        const { data } = await HTTPService.checkActivePaymentOption(option);
        this.initialResponseData = data;

        this.merchantQiwiToken = data.token;
        this.merchantPhoneNumber = data.phone;

        this.isQiwiMethodActive = true;
      } catch {
        this.isQiwiMethodActive = false;
      }
    },
    async disablePaymentOption() {
      await HTTPService.disablePaymentOption('qiwi');
      await this.checkActivePaymentOption('qiwi');
    },
  },
};
</script>

<style lang="sass" scoped>
  .modal-title
    position: relative

    h2
      padding-right: 40px

  .input-container
    &:not(:last-child)
      margin-bottom: 20px

  .modal-help-icon
    width: 30px
    height: 30px
    position: absolute
    top: 0
    right: 0
    background-image: url('~@/assets/img/icons/question.svg')
    background-repeat: no-repeat
    background-position: center
    cursor: pointer

  .buttons-container
    display: flex
    flex-direction: column
    align-items: center
    justify-content: space-between

    *
      &:not(:last-child)
        margin-bottom: 10px

    +respond-to('small')
      flex-direction: row

      *
        &:not(:last-child)
          margin-bottom: 0
</style>
